import React from 'react';
import Joyride, {
  CallBackProps,
  Step,
  ACTIONS,
  EVENTS,
  STATUS,
} from 'react-joyride';
import { useInProgressWorkOrders } from '@/hooks/useInProgressWorkOrders';
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useHistory } from 'react-router-dom';

const activeTourAtom = atom<string | undefined>(undefined);
const runTourAtom = atom<boolean>(false);
const activeTourStepAtom = atom<number>(0);
const tourStepsAtom = atom<Step[] | undefined>(undefined);

export const useTour = (): {
  run: boolean;
  setRun: (run: boolean) => void;
  stepIndex: number;
  setStepIndex: (index: number) => void;
  steps: Step[] | undefined;
  setSteps: (steps: Step[] | undefined) => void;
} => {
  const [run, setRun] = useAtom(runTourAtom);
  const [stepIndex, setStepIndex] = useAtom(activeTourStepAtom);
  const [steps, setSteps] = useAtom(tourStepsAtom);
  return {
    run,
    setRun,
    stepIndex,
    setStepIndex,
    steps,
    setSteps,
  };
};

type TourProviderProps = {
  children: React.ReactNode;
};

export const GuidedTourProvider: React.FC<TourProviderProps> = ({
  children,
}) => {
  const [run, setRun] = useAtom(runTourAtom);
  const [stepIndex, setStepIndex] = useAtom(activeTourStepAtom);
  const steps = useAtomValue(tourStepsAtom);
  const history = useHistory();

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { action, index, status, type, step } = data;
    const { next, previous, clickNext, clickPrevious, delay } =
      step?.data ?? {};
    const isBack = action === ACTIONS.PREV;

    if (
      [STATUS.FINISHED, STATUS.SKIPPED].includes(status as any) ||
      action === ACTIONS.CLOSE
    ) {
      // You need to set our running state to false, so we can restart if we click start again.
      setRun(false);
      window.localStorage.setItem('machinistWOTourDone', 'true');
    } else if (
      [EVENTS.STEP_AFTER, EVENTS.TARGET_NOT_FOUND].includes(type as any)
    ) {
      // Update state to advance the tour
      const nextIndex = index + (isBack ? -1 : 1);
      const nextTarget = steps?.[nextIndex]?.target;
      nextTarget && typeof nextTarget === 'string'
        ? waitForElm(nextTarget as string).then(() => setStepIndex(nextIndex))
        : setStepIndex(nextIndex);
      isBack ? previous && history.push(previous) : next && history.push(next);
      setTimeout(
        () =>
          isBack
            ? clickPrevious &&
              document.querySelector<HTMLElement>(clickPrevious)?.click()
            : clickNext &&
              document.querySelector<HTMLElement>(clickNext)?.click(),
        delay ?? 0
      );
    }
  };
  return (
    <>
      <Joyride
        steps={steps}
        callback={handleJoyrideCallback}
        run={run}
        stepIndex={stepIndex}
        continuous
        spotlightClicks
        locale={{
          back: 'Atras',
          close: 'Cerrar',
          last: 'Terminar Tour',
          next: 'Siguiente',
          skip: 'Saltar',
        }}
        styles={{
          options: {
            zIndex: 10,
            primaryColor: '#f25f5c',
          },
          tooltip: {
            pointerEvents: 'auto',
            zIndex: 30,
          },
        }}
      />
      {children}
    </>
  );
};

const machinistWOTourSteps: Step[] = [
  // 0
  {
    target: '.my-wos-footer',
    content:
      'Te han asignado tu primer Orden de Trabajo. Hace click aquí para ver tus OTs asignadas.',
    data: {
      next: '/my-work-orders?status=IN_PROGRESS',
    },
  },
  // 1
  {
    target: '.my-wos-table td a',
    content: (
      <>
        Esta vista muestra todas las ordenes de trabajo asignadas a vos. Podés
        filtrarlas por estado o por tipo.
        <br />
        <br />
        Presiona sobre el número de OT para verla.
      </>
    ),
    placement: 'bottom-end',
    data: {
      clickNext: '.my-wos-table td a',
      previous: '/',
    },
  },
  // 2
  {
    target: '.wo-details-sheet-trigger',
    content:
      'Esta vista representa una Orden de trabajo que te ha sido asignada. Presiona sobre esta ⓘ para ver los detalles de la misma.',
  },
  // 4
  {
    target: '.wo-history-sheet-trigger',
    content:
      'Presiona sobre el calendario para ver el historial de este equipo.',
  },
  // 6
  {
    target: '#new-task',
    content:
      'Podés agregar tareas a la OT presionando en este botón. Si tu OT no tiene tareas, asegurate de crear una para continuar el tour.',
  },
  // 7
  {
    target: 'td [role="checkbox"]',
    content:
      'Una vez realizada cada tarea, marcala como completada presionando sobre los casilleros en la columna "Completada"',
  },
  // 8
  {
    target: '.edit-wo-task-used-consumable',
    content: 'Podés cambiar el insumo utilizado en cada tarea presionando acá.',
  },
  // 9
  {
    target: '.wo-attachments',
    content: 'Podes adjuntar archivos a esta Orden de Trabajo.',
  },
  {
    target: '.wo-comments',
    content:
      'En esta sección podes registrar comentarios sobre esta Orden de Trabajo, o ver comentarios registrados anteriormente.',
  },
  {
    target: '.wo-actions',
    content:
      'Finalmente, acá arriba podés guardar tu avance en la OT. Cuando hayas terminado el trabajo, presioná "Finalizar Ejecución" para finalizar la OT.',
  },
];

export const MachinistWOTour = () => {
  const setActiveTour = useSetAtom(activeTourAtom);
  const { setSteps, setRun } = useTour();
  const { count: numInProgressWO } = useInProgressWorkOrders();

  React.useEffect(() => {
    if (
      !window.localStorage.getItem('machinistWOTourDone') &&
      numInProgressWO > 0
    ) {
      setSteps?.(machinistWOTourSteps);
      setRun(true);
      setActiveTour('machinistWOTour');
    }
  }, [numInProgressWO, setActiveTour, setRun, setSteps]);

  return null;
};

function waitForElm(selector: string): Promise<Element | null> {
  return new Promise((resolve) => {
    if (document.querySelector(selector)) {
      return resolve(document.querySelector(selector));
    }

    const observer = new MutationObserver(() => {
      if (document.querySelector(selector)) {
        observer.disconnect();
        resolve(document.querySelector(selector));
      }
    });

    // If you get "parameter 1 is not of type 'Node'" error, see https://stackoverflow.com/a/77855838/492336
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  });
}

export const useSetTourStep = (tour: string) => {
  const activeTour = useAtomValue(activeTourAtom);
  const { setStepIndex, stepIndex } = useTour();
  return (step: number | ((index: number) => number)) => {
    if (activeTour !== tour) return;
    setStepIndex(typeof step === 'function' ? step(stepIndex) : step);
  };
};

export const useSetTourStepOnMount = (tour: string, step: number) => {
  const activeTour = useAtomValue(activeTourAtom);
  const { setStepIndex } = useTour();
  const setStep = React.useRef(setStepIndex);
  React.useEffect(() => {
    if (tour === activeTour) {
      setStep.current(step);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};
